import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    OnDestroy,
    OnInit,
    ViewChild
} from "@angular/core";
import {first, Subscription} from "rxjs";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {FUIAlertActions, FUIAlertService, FUIResponsiveService, FUISpinnersService} from "@fedeltapointofsale/ui";
import {FilterMembers, FilterModel, provideFilterModel} from "../../../framework/bi/filter/filter.model";
import {
    ConfigurationService,
    provideEntityModel,
    provideReportModel,
    provideSearchModel
} from "@fedeltapointofsale/data-client";
import {PivotGridEntityModel} from "../pivot-grid-entity.model";
import {PivotGridModel} from "../pivot-grid.model";
import {BIService} from "../../../framework/bi/bi.service";
import {PivotGridData} from "../pivot-grid.data";
import {BIPageSearchModel} from "../../bi-page/bi-page-search.model";
import {BiPageSelectNavComponent} from "../../bi-page/controls/bi-page-select-nav.component";
import {CubeDefinitionData} from "../../../framework/bi/cube-definition.data";
import {PivotGridReportModel} from "../pivot-grid-report.model";
import {ReportGenerationService} from "../../../framework/entity/report/report-generation.service";
import {BIPageData} from "../../bi-page/bi-page.data";
import {Config, ServerAuth} from "../../../config";
import {ServerStatus, ServerStatusData} from "../../../framework/bi/server-status/server-status.data";
import {ServerStatusModel} from "../../../framework/bi/server-status/server-status.model";

@Component({
    selector: "wbo-bi-pivot-grid-view-page",
    templateUrl: "./pivot-grid-view-page.component.html",
    styleUrls: ["./pivot-grid-view-page.component.scss"],
    providers: [BIService, provideSearchModel(BIPageSearchModel), provideEntityModel(PivotGridEntityModel), ReportGenerationService, provideReportModel(PivotGridReportModel),
        PivotGridModel, provideFilterModel(),
        ServerStatusModel],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PivotGridViewPageComponent implements OnInit, OnDestroy {

    /**
     * Offset of the selection control
     */
    private static readonly HEIGHT_OFFSET: number = 325;
    @ViewChild(BiPageSelectNavComponent, {static: true}) selectNav: BiPageSelectNavComponent;
    id: string;
    cubeDefinition: CubeDefinitionData;
    /**
     * Grid size which is dynamic based on screen size and active controls
     */
    gridHeight: number;
    editable: boolean;
    clonable: boolean;
    deletable: boolean;
    @HostBinding("style.display") private display: string = "flex";
    @HostBinding("style.width") private width: string = "100%";
    private subscriptions: Subscription = new Subscription();

    constructor(public model: PivotGridModel,
                public biService: BIService,
                public entityModel: PivotGridEntityModel,
                public filterModel: FilterModel,
                private route: ActivatedRoute,
                private router: Router,
                private responsiveService: FUIResponsiveService,
                private alertService: FUIAlertService,
                private biPageSearchModel: BIPageSearchModel,
                private changeDetectorRef: ChangeDetectorRef,
                private configService: ConfigurationService,
                private spinnerService: FUISpinnersService,
                public serverStatusModel: ServerStatusModel,
                public reportGenerationService: ReportGenerationService) {
    }

    ngOnInit(): void {
        this.configService.getBoolean(Config.BI_ENABLED).then(biEnabled => {
            if (!biEnabled) {
                this.router.navigate(["/main/home"], {replaceUrl: true});
            }
        });

        this.route.params.subscribe((params: Params) => {
            this.id = params["id"];
            this.loadPivotGrid();
        });

        this.updateGridSize();

        this.subscriptions.add(this.filterModel.filterMembersUpdated$.subscribe((filterMembers: FilterMembers) => {
            if (filterMembers) {
                this.model.updateSlicer(filterMembers.members, filterMembers.dateRange);
            }
        }));

        this.subscriptions.add(this.responsiveService.window$.subscribe((window) => {
            this.updateGridSize();
        }));
    }

    loadPivotGrid(): void {
        this.spinnerService.open(true);
        this.selectNav.refreshData();
        this.entityModel.loadEntity(this.id).then((data: PivotGridData) => {
            this.entityModel.canCreateEntity().then((canCreate: boolean) => this.clonable = canCreate && this.entityModel.isEntityCloneable());

            this.configService.getBoolean(ServerAuth.PUBLISH_BI_DASHBOARD).then((canPublish: boolean) => {
                if (data.public && !canPublish) {
                    this.editable = false;
                    this.deletable = false;
                } else {
                    this.entityModel.canUpdateEntity().then((canUpdate: boolean) => this.editable = canUpdate && parseInt(this.id) >= 0);
                    this.entityModel.canDeleteEntity().then((canDelete: boolean) => this.deletable = canDelete && parseInt(this.id) >= 0);
                }
            });

            this.subscriptions.add(this.serverStatusModel.statusUpdate$.subscribe((status: ServerStatusData) => {
                this.spinnerService.close();
                if (status.status === ServerStatus.INITIALISED) {
                    this.loadCube();
                } else {
                    console.log("BI Server status: " + status.status);
                    console.log(status.percentComplete + "% complete");
                }
                this.changeDetectorRef.markForCheck();
            }));
        }).catch(error => {
            this.spinnerService.close();
            throw error;
        });
    }

    loadCube(): void {
        this.spinnerService.open(true);
        this.biService.getCube(this.entityModel.data.cubeName).then((cube) => {
            this.cubeDefinition = cube;

            this.model.init(cube, this.entityModel.data, this.reportGenerationService);
            this.filterModel.init(cube, this.entityModel.data.dateRangeRef, this.entityModel.data.slicers);

            this.changeDetectorRef.markForCheck();

            this.spinnerService.close();
        }).catch(error => {
            this.spinnerService.close();
            throw error;
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    reset(): void {
        this.loadPivotGrid();
    }

    edit(): void {
        if (this.id) {
            this.router.navigate(["/main/pivot-edit/" + this.id]);
        } else {
            this.router.navigate(["/main/pivot-edit/new"]);
        }
    }

    delete(): void {
        this.alertService.showConfirmationOkCancel("wbo.bi-pivot-grid.prompt.remove.heading", "wbo.bi-pivot-grid.prompt.remove.message").subscribe((action) => {
            if (action === FUIAlertActions.OK_ACTION) {
                this.entityModel.deleteEntity(this.id);

                this.biPageSearchModel.filter["pageType"] = BIPageData.PAGE_TYPE_PIVOT_GRID;

                this.biPageSearchModel.search().pipe(first()).subscribe(() => {
                    if (this.biPageSearchModel.data.length === 0) {
                        this.router.navigate(["/main"]);
                    } else {
                        this.router.navigate(["/main/pivot/" + this.biPageSearchModel.data[0].id]);
                    }
                });
            }
        });
    }

    clone(): void {
        this.router.navigate(["main/pivot-edit/new"], {queryParams: {clone: this.id}});
    }

    refresh(): void {
        this.filterModel.notifyFilterChange();
    }

    /**
     * Sets the grid size based on the components around it
     */
    private updateGridSize(): void {
        this.gridHeight = this.responsiveService.host().innerHeight - PivotGridViewPageComponent.HEIGHT_OFFSET;

        this.changeDetectorRef.markForCheck();
    }
}
